import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Banknote, Users } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { useCurrency } from '@/contexts/CurrencyContext'
import apiClient, { ApiClientError } from '@/api/client'
import { toastHelpers } from '@/lib/toast-helpers'
import type { BusinessDay, CashDrawerMovement, ShiftClosing } from '@/types'
import { NumericKeypad } from './NumericKeypad'
import { TenderCountStep } from './TenderCountStep'
import { StepIndicator } from './StepIndicator'
import { ReviewStatus } from './ReviewStatus'
import { ReviewTable, type ReviewRow } from './ReviewTable'
import { DEFAULT_VARIANCE_THRESHOLD, type ExpectedBreakdownSpec } from './tenderMath'

interface ShiftCloseWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  day: BusinessDay
  /** Whole-day tender totals so far (from getCurrentDayStatus). */
  cashSales: number
  cardSales: number
  /** Online sales EXCLUDING platform-settled receivables (F-MM-N23). */
  onlineSales: number
  /** F-MM-N23: online tender the marketplace pays out later — shown, never counted. */
  platformSettledOnline?: number
  /** Shift closes already recorded today (used to window this shift). */
  priorShifts: ShiftClosing[]
  /** Real open orders — shown as a non-blocking FYI (dinner spans shifts). */
  unresolvedOpenOrders: number
  movements: CashDrawerMovement[] | undefined
  isLoadingSummary: boolean
}

type Step = 'count-cash' | 'count-card' | 'count-online' | 'cash-drop' | 'review'

const STEP_ORDER: Step[] = ['count-cash', 'count-card', 'count-online', 'cash-drop', 'review']

/**
 * Shift close wizard (F-CTRL-N29) — a mid-day drawer reconciliation at
 * handover. The X-report sibling of CloseDayWizard: same plain-language,
 * cashier-first tone, but the day stays OPEN and there is no stage/lock —
 * a single shiftCloseDay call records the snapshot.
 *
 *   Step 1: count the cash in the drawer
 *   Step 2: card machine total
 *   Step 3: online & wallet total
 *   Step 4: move cash to the safe? (optional, continuous drawer)
 *   Step 5: review and finish
 *
 * The shift's expected cash is derived from the whole-day totals minus the
 * prior shifts' windows — the same arithmetic the backend seals authoritatively.
 *
 * F-CTRL-N33 split the old single, skippable, keypad-less "card and online"
 * screen into two mandatory keypad steps, matching the day close. The handover
 * carries the same risk: a shift that hands over an uncounted card batch leaves
 * a gap that surfaces (if ever) at the day close, by which point it cannot be
 * attributed to a shift or a person. Only the cash drop stays optional — that
 * is a decision about where to put money, not a count of money taken.
 */
export function ShiftCloseWizard({
  open,
  onOpenChange,
  day,
  cashSales,
  cardSales,
  onlineSales,
  platformSettledOnline = 0,
  priorShifts,
  unresolvedOpenOrders,
  movements,
  isLoadingSummary,
}: ShiftCloseWizardProps) {
  const { formatCurrency } = useCurrency()
  const qc = useQueryClient()

  const shiftNumber = priorShifts.length + 1
  const prior = priorShifts.length > 0 ? priorShifts[priorShifts.length - 1] : null

  // Continuous drawer: this shift opened on the prior shift's counted cash
  // minus its drop; shift 1 opens on the day's float.
  const shiftOpening = prior ? prior.counted_cash - prior.cash_drop : day.opening_cash ?? 0
  const windowStart = prior ? prior.closed_at : day.opened_at

  // This shift's window figures = whole-day totals minus what prior shifts
  // already reconciled (their windows), mirroring the backend split.
  const priorCash = priorShifts.reduce((s, sh) => s + sh.shift_cash_sales, 0)
  const priorCard = priorShifts.reduce((s, sh) => s + sh.shift_card_sales, 0)
  const priorOnline = priorShifts.reduce((s, sh) => s + sh.shift_online_sales, 0)
  const shiftCashSales = Math.max(0, cashSales - priorCash)
  const shiftCardSales = Math.max(0, cardSales - priorCard)
  const shiftOnlineSales = Math.max(0, onlineSales - priorOnline)

  // Net adjustments inside THIS shift's window, excluding handover drops
  // (those live in the next shift's opening) — matches shiftWindowedNetAdj.
  const shiftNetAdj = useMemo(() => {
    const start = new Date(windowStart).getTime()
    return (movements ?? [])
      .filter((m) => new Date(m.created_at).getTime() >= start)
      .filter((m) => !(m.movement_type === 'paid_out' && (m.notes ?? '').includes('shift_handover=')))
      .reduce((acc, m) => (m.movement_type === 'paid_in' ? acc + m.amount : acc - m.amount), 0)
  }, [movements, windowStart])

  const expectedCash = shiftOpening + shiftCashSales + shiftNetAdj

  const [step, setStep] = useState<Step>('count-cash')
  const [countedCash, setCountedCash] = useState('')
  const [countedCard, setCountedCard] = useState('')
  const [countedOnline, setCountedOnline] = useState('')
  const [cashDrop, setCashDrop] = useState('')
  const [notes, setNotes] = useState('')

  useEffect(() => {
    if (open) {
      setStep('count-cash')
      setCountedCash('')
      setCountedCard('')
      setCountedOnline('')
      setCashDrop('')
      setNotes('')
    }
  }, [open])

  const parsedCash = Number(countedCash) || 0
  const parsedCard = Number(countedCard) || 0
  const parsedOnline = Number(countedOnline) || 0
  const parsedDrop = Number(cashDrop) || 0
  const cashVariance = parsedCash - expectedCash
  const cardEntered = countedCard.trim() !== '' && Number.isFinite(Number(countedCard))
  const onlineEntered = countedOnline.trim() !== '' && Number.isFinite(Number(countedOnline))
  const isCountValid = countedCash.trim() !== '' && Number.isFinite(Number(countedCash))
  const dropTooBig = parsedDrop > parsedCash + 0.001
  const carriedForward = Math.max(0, parsedCash - parsedDrop)

  const thresholdQuery = useQuery({
    queryKey: ['settings', 'day_close_variance_threshold'],
    queryFn: () => apiClient.getSetting('day_close_variance_threshold'),
    staleTime: 5 * 60_000,
  })
  const varianceThreshold = useMemo(() => {
    const raw = thresholdQuery.data?.data
    const n = typeof raw === 'number' ? raw : Number(raw)
    return Number.isFinite(n) && n >= 0 ? n : DEFAULT_VARIANCE_THRESHOLD
  }, [thresholdQuery.data])

  const absVariance = Math.abs(cashVariance)
  const isBigOff = absVariance > varianceThreshold

  const cardShort = cardEntered ? Math.max(0, shiftCardSales - parsedCard) : 0
  const cardOver = cardEntered ? Math.max(0, parsedCard - shiftCardSales) : 0
  const onlineShort = onlineEntered ? Math.max(0, shiftOnlineSales - parsedOnline) : 0
  const onlineOver = onlineEntered ? Math.max(0, parsedOnline - shiftOnlineSales) : 0
  const cardUnexplained = cardShort + cardOver
  const onlineUnexplained = onlineShort + onlineOver

  // Variance note gate (accountability), mirroring the day close — but NO
  // open-orders gate: dinner service spans shifts, so open tabs are expected
  // mid-shift and are shown as information only.
  const varianceNeedsNote =
    isBigOff || cardUnexplained > varianceThreshold || onlineUnexplained > varianceThreshold
  const noteRequired = varianceNeedsNote && notes.trim().length === 0

  // Every tender, for the headline and the breakdown. The banner used to report
  // cash alone, so a handover could read "Rs 500 short" while the card batch was
  // thousands out. Not summed: separate pots, and netting would hide one fault
  // behind another. See CloseDayWizard's SummaryBanner for the full reasoning.
  const shiftTenders = [
    {
      label: 'Cash',
      over: cashVariance > 0 ? cashVariance : 0,
      short: cashVariance < 0 ? -cashVariance : 0,
      needsNote: absVariance > varianceThreshold,
    },
    { label: 'Card', over: cardOver, short: cardShort, needsNote: cardUnexplained > varianceThreshold },
    {
      label: 'Online',
      over: onlineOver,
      short: onlineShort,
      needsNote: onlineUnexplained > varianceThreshold,
    },
  ]
  const shiftOffTenders = shiftTenders.filter((t) => t.over > 0.005 || t.short > 0.005)

  // The arithmetic behind each expected figure (F-FE-N139). A shift has no
  // cross-day bridge, so card and online are a single line each.
  const cashBreakdown: ExpectedBreakdownSpec = {
    rows: [
      { label: 'Shift started with', amount: shiftOpening },
      { label: 'Cash sales this shift', amount: shiftCashSales, sign: '+' },
      ...(shiftNetAdj !== 0
        ? [
            {
              label: shiftNetAdj > 0 ? 'Money put in' : 'Money taken out',
              amount: Math.abs(shiftNetAdj),
              sign: shiftNetAdj > 0 ? ('+' as const) : ('−' as const),
            },
          ]
        : []),
    ],
    total: { label: 'Should be in the drawer', amount: expectedCash },
  }
  const cardBreakdown: ExpectedBreakdownSpec = {
    rows: [{ label: "This shift's card sales", amount: shiftCardSales }],
    total: { label: 'On the machine this shift', amount: shiftCardSales },
  }
  const onlineBreakdown: ExpectedBreakdownSpec = {
    rows: [
      { label: "This shift's online sales", amount: shiftOnlineSales },
      ...(platformSettledOnline > 0
        ? [{ label: 'Not counted: Foodpanda pays you later', amount: platformSettledOnline, tone: 'muted' as const }]
        : []),
    ],
    total: { label: 'Should be in your accounts', amount: shiftOnlineSales },
    why:
      platformSettledOnline > 0 && shiftOnlineSales < 0.005
        ? 'Every online order this shift was Foodpanda, and that money is not in your bank. Type 0 here; Foodpanda is checked at the day close.'
        : platformSettledOnline > 0
          ? 'Foodpanda money is not in your bank tonight. It is checked at the day close, so leave it out here.'
          : undefined,
  }
  const reviewRows: ReviewRow[] = [
    {
      key: 'cash',
      label: 'Cash',
      sublabel: parsedDrop > 0 ? `${formatCurrency(parsedDrop)} moved to the safe, ${formatCurrency(carriedForward)} carried forward` : undefined,
      expected: expectedCash,
      counted: parsedCash,
      delta: cashVariance,
      breakdown: cashBreakdown,
    },
    {
      key: 'card',
      label: 'Card machine',
      expected: shiftCardSales,
      counted: parsedCard,
      delta: parsedCard - shiftCardSales,
      breakdown: cardBreakdown,
    },
    {
      key: 'online',
      label: 'Online & wallet',
      expected: shiftOnlineSales,
      counted: parsedOnline,
      delta: parsedOnline - shiftOnlineSales,
      breakdown: onlineBreakdown,
    },
  ]
  const offSummary = (() => {
    const off = shiftTenders.filter((t) => t.needsNote)
    if (off.length === 0) return ''
    const names = off.map((t) => t.label)
    const list =
      names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
    const allShort = off.every((t) => t.short > 0)
    const allOver = off.every((t) => t.over > 0)
    const verb = off.length === 1 ? 'is' : 'are'
    return allShort ? `${list} ${verb} short` : allOver ? `${list} ${verb} over` : `${list} don't match`
  })()

  const shiftMut = useMutation({
    mutationFn: () =>
      apiClient.shiftCloseDay(day.id, {
        counted_cash: parsedCash,
        // Always sent (F-CTRL-N33) — the steps can't be advanced without a
        // number, so a 0 here means "took nothing", never "not counted".
        counted_card: parsedCard,
        counted_online: parsedOnline,
        cash_drop: parsedDrop > 0 ? parsedDrop : undefined,
        notes: notes.trim() || undefined,
      }),
  })

  const finish = async () => {
    try {
      const res = await shiftMut.mutateAsync()
      if (!res.success) {
        toastHelpers.error(res.message || 'Failed to close shift')
        return
      }
      toastHelpers.success(`Shift ${shiftNumber} closed`)
      qc.invalidateQueries({ queryKey: ['day-ops'] })
      qc.invalidateQueries({ queryKey: ['shift-closings'] })
      qc.invalidateQueries({ queryKey: ['currentDayStatus'] })
      onOpenChange(false)
    } catch (err) {
      qc.invalidateQueries({ queryKey: ['day-ops'] })
      // Unreachable in normal use — kept for a stale tab on an older bundle.
      if (err instanceof ApiClientError && err.code === 'tender_count_required') {
        setStep((err.missingTenders ?? []).includes('online') ? 'count-online' : 'count-card')
        toastHelpers.error(
          'Count this first',
          'Enter the total for every payment type — type 0 if it took nothing.',
        )
        return
      }
      toastHelpers.apiError('Close shift', err as Error)
    }
  }

  const finishing = shiftMut.isPending

  // Linear navigation; every counting step is mandatory (F-CTRL-N33). The cash
  // drop stays optional — blank means "carry the whole drawer forward".
  const stepIndex = STEP_ORDER.indexOf(step)
  const goNext = () => setStep(STEP_ORDER[Math.min(stepIndex + 1, STEP_ORDER.length - 1)])
  const goBack = () => setStep(STEP_ORDER[Math.max(stepIndex - 1, 0)])
  const canAdvance =
    step === 'count-cash'
      ? isCountValid
      : step === 'count-card'
        ? cardEntered
        : step === 'count-online'
          ? onlineEntered
          : step === 'cash-drop'
            ? !dropTooBig
            : true

  return (
    <Dialog open={open} onOpenChange={(o) => !finishing && onOpenChange(o)}>
      <DialogContent className="flex max-h-[min(92dvh,900px)] max-w-2xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 space-y-1 border-b px-6 py-4 pr-12 text-left">
          <DialogTitle className="text-2xl">
            {step === 'count-cash' && `Close shift ${shiftNumber}: count the drawer`}
            {step === 'count-card' && 'Card machine'}
            {step === 'count-online' && 'Online & wallet'}
            {step === 'cash-drop' && 'Move cash to the safe?'}
            {step === 'review' && `Shift ${shiftNumber} count`}
          </DialogTitle>
          <DialogDescription className="text-base">
            {step === 'count-cash' &&
              'Handover time. Count every note and coin in the till and type the total.'}
            {step === 'count-card' &&
              "Type this shift's total from the card machine. If it took nothing, type 0."}
            {step === 'count-online' &&
              (platformSettledOnline > 0
                ? 'Money that reached your bank, Easypaisa or JazzCash this shift. Foodpanda is checked at the day close.'
                : 'Money that reached your bank, Easypaisa or JazzCash this shift. If none, type 0.')}
            {step === 'cash-drop' &&
              'Leaving a big pile of cash in the drawer is risky. Move some to the safe now — the next shift starts fresh. Optional.'}
            {step === 'review' &&
              'Check each line, write a note if something is off, then finish the shift. The day stays open.'}
          </DialogDescription>
          <StepIndicator step={step} order={STEP_ORDER} />
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {step === 'count-cash' && (
            <TenderCountStep
              id="shift-counted-cash"
              label="Money you counted in the drawer"
              value={countedCash}
              onChange={setCountedCash}
              formatCurrency={formatCurrency}
            />
          )}

          {step === 'count-card' && (
            <TenderCountStep
              id="shift-counted-card"
              label="Total on the card machine this shift"
              value={countedCard}
              onChange={setCountedCard}
              formatCurrency={formatCurrency}
              expected={shiftCardSales}
              delta={cardEntered ? parsedCard - shiftCardSales : undefined}
              threshold={varianceThreshold}
              breakdown={cardBreakdown}
              idleText="Type the machine total to compare"
            />
          )}

          {step === 'count-online' && (
            <TenderCountStep
              id="shift-counted-online"
              label="Online and wallet total this shift"
              value={countedOnline}
              onChange={setCountedOnline}
              formatCurrency={formatCurrency}
              expected={shiftOnlineSales}
              delta={onlineEntered ? parsedOnline - shiftOnlineSales : undefined}
              threshold={varianceThreshold}
              breakdown={onlineBreakdown}
              idleText="Type the bank and wallet total to compare"
            />
          )}

          {step === 'cash-drop' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-xl border-2 border-sky-200 bg-sky-50 p-4 dark:border-sky-900 dark:bg-sky-950/40">
                <div className="rounded-full bg-sky-500 p-1.5 shrink-0">
                  <Banknote className="h-5 w-5 text-white" />
                </div>
                <div className="text-sm text-sky-900 dark:text-sky-100">
                  You counted <span className="font-semibold">{formatCurrency(parsedCash)}</span>. Move
                  some of it to the safe and the next shift opens on a smaller, safer float. Leave this
                  blank to carry the whole drawer forward.
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="shift-cash-drop" className="text-base">
                  Amount to move to the safe (optional)
                </Label>
                <Input
                  id="shift-cash-drop"
                  type="text"
                  inputMode="decimal"
                  value={cashDrop}
                  onChange={(e) => setCashDrop(e.target.value.replace(/[^0-9.]/g, ''))}
                  className={cn(
                    'h-14 text-2xl font-semibold tabular-nums',
                    dropTooBig && 'border-rose-400 focus-visible:ring-rose-400',
                  )}
                />
                {dropTooBig ? (
                  <p className="text-sm font-medium text-rose-600">
                    You can't move more than you counted ({formatCurrency(parsedCash)}).
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Next shift will start with{' '}
                    <span className="font-medium text-foreground">{formatCurrency(carriedForward)}</span>
                  </p>
                )}
              </div>
              <NumericKeypad value={cashDrop} onChange={setCashDrop} />
            </div>
          )}

          {step === 'review' && (
            <div className="space-y-4">
              <ReviewStatus
                offTenders={shiftOffTenders}
                totalTenders={shiftTenders.length}
                needsNote={varianceNeedsNote}
                loading={isLoadingSummary}
                formatCurrency={formatCurrency}
              />

              {/* F-CTRL-N33: all three tenders side by side. Every one has been
                  counted by this point, so the handover summary is complete
                  rather than cash-only. */}
              <ReviewTable
                rows={reviewRows}
                threshold={varianceThreshold}
                formatCurrency={formatCurrency}
              />

              {unresolvedOpenOrders > 0 && (
                <div className="flex items-start gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm">
                  <Users className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="text-muted-foreground">
                    <span className="font-medium text-foreground">
                      {unresolvedOpenOrders} table{unresolvedOpenOrders === 1 ? '' : 's'} still open
                    </span>{' '}
                    — that's fine mid-shift. They carry over to the next shift and settle at the day close.
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="shift-notes" className="text-base">
                  Note for the owner
                  {varianceNeedsNote ? (
                    <span className="ml-1 text-sm font-normal text-rose-700 dark:text-rose-300">
                      (required, {offSummary})
                    </span>
                  ) : (
                    <span className="ml-1 text-sm font-normal text-muted-foreground">(optional)</span>
                  )}
                </Label>
                <Textarea
                  id="shift-notes"
                  rows={2}
                  placeholder="e.g. drawer short, gave change from own pocket, will settle"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className={cn(noteRequired && 'border-rose-400 focus-visible:ring-rose-400')}
                />
              </div>

              <p className="text-sm text-muted-foreground">
                This records the shift handover. The day stays open and sales keep counting.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 gap-2 border-t px-6 py-4">
          {/* No Skip on any counting step (F-CTRL-N33). */}
          {step !== 'review' && (
            <>
              <Button
                variant="outline"
                size="lg"
                className="h-12"
                onClick={step === 'count-cash' ? () => onOpenChange(false) : goBack}
              >
                {step === 'count-cash' ? 'Cancel' : 'Back'}
              </Button>
              <Button
                size="lg"
                className="h-12 flex-1 sm:flex-none"
                onClick={goNext}
                disabled={!canAdvance}
              >
                Next <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </>
          )}
          {step === 'review' && (
            <>
              <Button
                variant="outline"
                size="lg"
                className="h-12"
                onClick={goBack}
                disabled={finishing}
              >
                Back
              </Button>
              <Button
                size="lg"
                className="h-12 flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={finish}
                disabled={finishing || noteRequired || isLoadingSummary}
              >
                {finishing ? 'Saving…' : `Finish shift ${shiftNumber}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
